import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { Box } from 'src/components/Box';
import { Button } from 'src/components/Button';
import { Grid } from 'src/components/Grid';
import { HtmlTypography } from 'src/components/HtmlTypography';
import { Link } from 'src/components/Link';
import { Meta } from 'src/components/Meta';
import { StickyCard, StickyCardSkeleton } from 'src/components/StickyCard';
import { Typography } from 'src/components/Typography';
import { useSession } from 'src/contexts/SessionContext';
import { externalLinks } from 'src/externalLinks';
import { fetchAssessment } from 'src/models/assessment/assessment';
import { AppFrameConfig } from 'src/shared/AppFrame/AppFrameConfig';
import { usePermissionCheck } from 'src/shared/usePermissionCheck';

import AssessmentCustomTemplate from './Widgets/AssessmentCustomTemplate';
import { UserAssessmentQuestion } from './Widgets/UserAssessmentQuestion';

export const AssessmentPreviewPage = () => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'assessment',
  });
  const { assessmentId } = useParams<{ assessmentId: string }>();
  const navigate = useNavigate();
  const { session } = useSession();
  const { canViewAssessment } = usePermissionCheck();

  const curDomain = externalLinks.railsBase || window.location.origin;

  const { data: assessment, isLoading } = useQuery({
    queryKey: ['assessment', assessmentId],
    queryFn: () => fetchAssessment(Number(assessmentId)),
    enabled: !!assessmentId,
  });

  useEffect(() => {
    if (!canViewAssessment) {
      navigate('/dashboard');
    }
  }, [canViewAssessment, navigate]);

  const publicLink = assessment
    ? `${curDomain}/assessment/${assessment.id}`
    : '';

  const privateLink = assessment
    ? `${curDomain}/dashboard/me/assessment/take/${assessment.id}`
    : '';

  const buttonTextWithDefault = assessment?.buttonText || t('submitAssessment');

  if (isLoading) {
    return <StickyCardSkeleton />;
  }

  if (!assessment) {
    return null;
  }

  const pageTitle = `${t('preview')} ${t('one')}`;

  return (
    <>
      <Meta title={pageTitle} brand={session?.brand} />
      <AppFrameConfig
        id="assessment-preview"
        containerMaxWidth="lg"
        title={pageTitle}
        backButtonLinkTo={() => navigate('/trainer/assessments')}
        backButtonTitle={t('createdAssessments')}
      />

      <StickyCard>
        {!assessment.customTemplate && (
          <Typography variant="h4" sx={{ mb: 3 }}>
            {assessment.name}
          </Typography>
        )}

        <Box component="form" sx={{ mb: 4 }}>
          {assessment.customTemplate ? (
            <AssessmentCustomTemplate assessment={assessment} />
          ) : (
            <>
              <HtmlTypography>{assessment.description}</HtmlTypography>
              {assessment.fields.map((field, index) => (
                <Grid container key={index} sx={{ mb: 2 }}>
                  <Grid size={{ xs: 12 }}>
                    <UserAssessmentQuestion
                      field={field}
                      index={index}
                      fields={assessment.fields}
                    />
                  </Grid>
                </Grid>
              ))}
            </>
          )}

          <Box sx={{ mt: 4 }}>
            <Button variant="contained" disabled fullWidth={false}>
              {buttonTextWithDefault}
            </Button>
          </Box>
        </Box>

        <Box sx={{ mt: 4, pt: 4, borderTop: 1, borderColor: 'divider' }}>
          <Grid container spacing={3}>
            <Grid size={{ xs: 12 }}>
              <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                {t('shareablePublicLink')}:
              </Typography>
              <Link
                href={publicLink}
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  display: 'block',
                  wordBreak: 'break-all',
                }}
              >
                {publicLink}
              </Link>
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                {t('shareablePrivateLink')}:
              </Typography>
              <Link
                href={privateLink}
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  display: 'block',
                  wordBreak: 'break-all',
                }}
              >
                {privateLink}
              </Link>
            </Grid>
          </Grid>
        </Box>
      </StickyCard>
    </>
  );
};
